import React, { useState, useMemo, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { Home, Scale, Newspaper, Book, Gavel, Scissors, Headphones, Video, FileText, Layers, Library, Clapperboard, Landmark, Globe, Building2, LogIn } from 'lucide-react';

import TopMenuMobile from './TopMobile';
import NavigationMenu from './NavigationMenu';
import GlobalSearchForm from '../components/GlobalSearchForm';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeSwitcher from '../components/ThemeSwitcher';
import { borderColor, pageGutter } from '../styles/styles';
import clsx from 'clsx';
import { useTheme } from '../context/ThemeProvider';
import lhrc_logo from '../assets/logo/lhrc_logo.svg';
import lhrc_logo_light from '../assets/logo/lhrc_logo_light.svg';

const AuthButtons = React.memo(({ tr }) => (
    <div className="hidden md:flex lg:flex space-x-2 justify-center shrink-0">
        <NavLink
            to="/login"
            className="text-white bg-[#41A6D7] hover:bg-[#3590BA] 
                       dark:bg-[#256B8A] dark:hover:bg-[#1E5A77]
                       focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-800 
                       font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
        >
            {tr('login')}
        </NavLink>
        {/* <NavLink
            to="/register"
            className="text-white bg-[#41A6D7] hover:bg-[#3590BA] 
                       dark:bg-[#256B8A] dark:hover:bg-[#1E5A77]
                       focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-800 
                       font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
        >
            {tr('register')}
        </NavLink> */}
        <LanguageSwitcher />
        <ThemeSwitcher />
    </div>
));


const Header = () => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`header:${key}`), [t]);

    const { theme } = useTheme();
    const logo = theme === 'dark' ? lhrc_logo_light : lhrc_logo;

    const menuItems = useMemo(
        () => [
            { to: '/', label: tr('home'), icon: Home },
            {
                id: 'constitutions',
                label: tr('constitution'),
                icon: Scale,
                children: [
                    { to: '/tanzania-constitution', label: tr('constitutionTanzania'), icon: Landmark },
                    { to: '/constitution', label: tr('constitutionsOtherCountries'), icon: Globe },
                ],
            },
            {
                id: 'books-journals',
                label: tr('booksAndJournals'),
                icon: Library,
                children: [
                    { to: '/journals', label: tr('journals'), icon: Newspaper },
                    { to: '/books', label: tr('books'), icon: Book },
                ],
            },
            { to: '/reports', label: tr('reports'), icon: FileText },
            {
                id: 'cases',
                label: tr('cases'),
                icon: Gavel,
                children: [
                    { to: '/cases', label: tr('localCourts'), icon: Building2 },
                    { to: '/regional-cases', label: tr('regionalCourts'), icon: Scale },
                ],
            },
            { to: '/cuttings', label: tr('cuttings'), icon: Scissors },
            {
                id: 'audiovisuals',
                label: tr('audiovisuals'),
                icon: Clapperboard,
                children: [
                    { to: '/audios', label: tr('audios'), icon: Headphones },
                    { to: '/videos', label: tr('videos'), icon: Video },
                ],
            },
            { to: '/other', label: tr('other'), icon: Layers },
        ],
        [tr]
    );

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleMobileMenu = useCallback(() => {
        setIsMobileMenuOpen((prev) => !prev);
    }, []);

    return (
        <header className="sticky w-full top-0 z-50 mb-6">
            <nav className={clsx('bg-white dark:bg-gray-800', borderColor, 'border-b')}>
                <div
                    className={clsx(
                        'flex flex-wrap justify-between',
                        'max-w-screen-3xl mx-auto',
                        pageGutter,
                        'items-center py-2 lg:py-3'
                    )}
                >
                    <a href="/" className="flex items-center space-x-3 rtl:space-x-reverse">
                        <img src={logo} alt="LHRC Logo" className="w-[70px] sm:w-[85px] md:w-[90px] lg:w-[100px] h-auto mb-0" />
                        <div className={clsx('text-[#50A1CA] dark:text-white font-bold text-4xl  mb-1')}>Katiba</div>
                    </a>
                    <div className="flex items-center gap-2 lg:gap-3">
                        <GlobalSearchForm tr={tr} className="w-44 sm:w-56 md:w-64 lg:w-72 max-w-sm shrink-0" />
                        <AuthButtons tr={tr} />
                        <div className="lg:hidden">
                            <button onClick={toggleMobileMenu} className="text-gray-600" aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMobileMenuOpen}>
                                {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>
            <nav className={clsx('hidden lg:flex border-t border-b mb-2', 'bg-white dark:bg-gray-800', borderColor)}>
                <div className={clsx('w-full max-w-screen-3xl mx-auto lg:block py-2', pageGutter)}>
                    <NavigationMenu items={menuItems} />
                </div>
            </nav>
            {isMobileMenuOpen && (
                <nav className="lg:hidden bg-transparent shadow-lg shadow-gray-300/40 fixed top-14 left-0 w-full z-50">
                    <div className="max-w-screen-xl py-3">
                        <TopMenuMobile
                            items={menuItems}
                            toggleMobileMenu={toggleMobileMenu}
                            loginTo="/login"
                            loginLabel={tr('login')}
                            LoginIcon={LogIn}
                        />
                    </div>
                </nav>
            )}
        </header>
    );
};

export default React.memo(Header);
